<template>
  <div class="category-tree-component">
    <!-- 固定按鈕區域 -->
    <div class="fixed-header">
      <div class="category-tree-actions">
        <button class="tree-expand-btn" @click="expandAll">展開全部</button>
        <button class="tree-collapse-btn" @click="collapseAll">收起全部</button>
        <button 
          class="tree-drag-toggle-btn"
          @click="toggleDragEnabled" 
          :class="{ 'disabled': globalDragDisabled }"
        >
          {{ globalDragDisabled ? '啟用拖曳' : '禁用拖曳' }}
        </button>
        <button class="tree-add-btn" @click="$emit('request-add')">新增分類</button>
      </div>
      
      <!-- 拖曳提示 -->
      <div v-if="showDragDisabledMessage" class="drag-disabled-message">
        <p>拖曳功能已暫時禁用，請點擊"啟用拖曳"按鈕重新啟用。</p>
      </div>
    </div>
    
    <!-- 滾動內容區域，確保有足夠的頂部空間 -->
    <div class="scroll-content">
      <!-- 重新使用 draggable 管理頂層列表 -->
      <draggable
        v-model="localTreeData"
        tag="ul"
        item-key="id"
        :group="{ name: 'categories', pull: true, put: true }"
        :animation="150"
        class="draggable-list top-level-list"
        @end="onTopLevelDragEnd"
        @add="onTopLevelDragAdd"
        @remove="onTopLevelDragRemove"
        @start="onTopLevelDragStart"
        :disabled="globalDragDisabled"
      >
        <template #item="{ element: node }"> 
          <tree-node
            :node="node"
            :flat-categories="flatCategories" 
            :depth="0"
            @edit="$emit('edit', $event)"
            @delete="$emit('delete', $event)"
            @request-add-child="handleRequestAddChild"
            @request-edit="handleRequestEdit"
            @request-delete="handleRequestDelete"
            @update="onChildUpdate" 
          />
        </template>
      </draggable>
    </div>
  </div>
</template>

<script setup>
import { ref, provide, watch, computed } from 'vue';
import draggable from 'vuedraggable'; // 重新導入 draggable
import TreeNode from './TreeNode.vue';

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  flatCategories: {
    type: Array,
    required: true
  }
});

// 更新 emit，重新加入 request 事件
const emit = defineEmits(['edit', 'delete', 'update-tree', 'request-add-child', 'request-edit', 'request-delete', 'request-add']);

// 本地可修改的樹數據副本
const localTreeData = ref([]);

// 新增：全局拖曳狀態控制
const globalDragDisabled = ref(false);
const showDragDisabledMessage = ref(false);

// 提供給子組件的全局拖曳控制
provide('globalDragDisabled', globalDragDisabled);

// 切換拖曳啟用狀態
function toggleDragEnabled() {
  globalDragDisabled.value = !globalDragDisabled.value;
  showDragDisabledMessage.value = globalDragDisabled.value;
}

// 監聽傳入的 data prop，更新本地副本
watch(() => props.data, (newData) => {
  console.log('[CategoryTree] props.data changed, updating localTreeData.');
  localTreeData.value = JSON.parse(JSON.stringify(newData));
}, { deep: true, immediate: true }); 

// 用於控制所有節點的展開/收起 (透過 provide/inject)
const expandAllNodes = ref(false);
provide('expandAllNodes', expandAllNodes); // TreeNode 會 inject 這個

function expandAll() {
  expandAllNodes.value = true;
}

function collapseAll() {
  expandAllNodes.value = false;
}

// --- 拖曳事件處理 ---
// 新增：拖曳開始事件
function onTopLevelDragStart(event) {
  console.log('[CategoryTree] Top level draggable @start event');
}

function onTopLevelDragEnd(event) {
  console.log('[CategoryTree] Top level draggable @end event');
  
  try {
    // 確保每個節點都有 display_order
    updateDisplayOrder(localTreeData.value);
    
    // 向上發射 update-tree 事件，通知父組件樹結構已更新
    emit('update-tree', localTreeData.value);
  } catch (err) {
    console.error('頂層拖曳結束出錯:', err);
    
    // 發生錯誤時，禁用拖曳功能，避免伺服器崩潰
    if (err.code === 'ERR_NETWORK') {
      globalDragDisabled.value = true;
      showDragDisabledMessage.value = true;
      alert('伺服器連接問題，已暫時禁用拖曳功能。');
    }
  }
}

function onTopLevelDragAdd(event) {
  console.log('[CategoryTree] Top level draggable @add event');
  
  try {
    // 當有新節點被拖入頂層時觸發
    updateDisplayOrder(localTreeData.value);
    emit('update-tree', localTreeData.value);
  } catch (err) {
    console.error('處理拖入頂層出錯:', err);
  }
}

function onTopLevelDragRemove(event) {
  console.log('[CategoryTree] Top level draggable @remove event');
  
  try {
    // 當有節點從頂層被拖出時觸發
    updateDisplayOrder(localTreeData.value);
    emit('update-tree', localTreeData.value);
  } catch (err) {
    console.error('處理從頂層拖出出錯:', err);
  }
}

function onChildUpdate() {
  console.log('[CategoryTree] Received update event from child TreeNode.');
  
  try {
    // 子層列表拖放結束，子組件的 v-model 更新了對應 node.children
    updateDisplayOrder(localTreeData.value);
    emit('update-tree', localTreeData.value);
  } catch (err) {
    console.error('處理子節點更新出錯:', err);
    
    // 發生錯誤時，禁用拖曳功能，避免伺服器崩潰
    if (err.code === 'ERR_NETWORK') {
      globalDragDisabled.value = true;
      showDragDisabledMessage.value = true;
    }
  }
}

// 新增：更新節點顯示順序的函數
function updateDisplayOrder(nodes, startIndex = 0) {
  if (!nodes || !Array.isArray(nodes)) return;
  
  // 更新當前層級所有節點的顯示順序
  nodes.forEach((node, index) => {
    node.display_order = startIndex + index;
    
    // 遞歸更新子節點顯示順序
    if (node.children && node.children.length > 0) {
      updateDisplayOrder(node.children, 0);
    }
  });
}

// --- 恢復按鈕事件處理函數 --- 
const handleRequestAddChild = (node) => {
  console.log(`[CategoryTree] Received request-add-child for node: ${node.name}, emitting upwards.`);
  emit('request-add-child', node);
};

const handleRequestEdit = (node) => {
  console.log(`[CategoryTree] Received request-edit for node: ${node.name}, emitting upwards.`);
  emit('request-edit', node);
};

const handleRequestDelete = (node) => {
  console.log(`[CategoryTree] Received request-delete for node: ${node.name}, emitting upwards.`);
  emit('delete', node);
};

// --- 移除原生拖放相關的狀態和處理函數 ---
/*
const draggingNodeId = ref(null);
const potentialDropTargetId = ref(null);
const currentDropPosition = ref(null);
const handleDragStartNode = (node) => { ... };
const handleDragEndNode = () => { ... };
const handlePotentialDropTarget = (nodeId, position) => { ... };
const handleClearPotentialDropTarget = () => { ... };
const handleDropOnNode = (targetNodeId, dropPosition) => { ... };
function findNodePath(...) { ... }
function moveNode(...) { ... }
*/

</script>

<style scoped>
.category-tree-component {
  margin: 0;
  padding: 0; /* 移除填充 */
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.03);
  scrollbar-width: thin;
  scrollbar-color: #909399 #f4f4f5;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%; /* 使組件填滿父容器高度 */
}

/* 固定頂部區域 */
.fixed-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;
  padding: 15px 20px 0;
}

.category-tree-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 15px;
}

/* 滾動內容區域 */
.scroll-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;
}

/* 拖曳提示 */
.drag-disabled-message {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  border-left: 3px solid #f56c6c;
}

.drag-disabled-message p {
  margin: 0;
  font-size: 14px;
}

.tree-expand-btn,
.tree-collapse-btn {
  padding: 6px 16px;
  font-size: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tree-expand-btn::before {
  content: '▼';
  font-size: 10px;
}

.tree-collapse-btn::before {
  content: '▶';
  font-size: 10px;
}

.tree-expand-btn:hover,
.tree-collapse-btn:hover {
  background-color: #f5f7fa;
  border-color: #c6e2ff;
  color: #409EFF;
}

.draggable-list {
  padding-left: 0;
  margin: 0;
  padding-top: 10px; /* 添加上邊距 */
}

.top-level-list {
  border-radius: 4px;
  padding: 4px;
}

/* 拖曳時的全局樣式 */
:deep(.sortable-ghost) {
  opacity: 0.5;
  background: #e6f1fc !important;
  border: 1px dashed #409EFF !important;
}

:deep(.sortable-chosen) {
  background: #f5f7fa;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

/* 優化滾動條樣式 */
.category-tree-component::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.category-tree-component::-webkit-scrollbar-track {
  background: #f4f4f5;
  border-radius: 3px;
}

.category-tree-component::-webkit-scrollbar-thumb {
  background: #909399;
  border-radius: 3px;
}

.category-tree-component::-webkit-scrollbar-thumb:hover {
  background: #606266;
}

/* 拖曳時的視覺提示 */
.top-level-list.drag-over {
  background-color: #f5f7fa;
  border: 2px dashed #409EFF;
}

/* 拖曳時的放置區域提示 */
.top-level-list {
  min-height: 50px;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.top-level-list:empty {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px dashed #dcdfe6;
}

.top-level-list:empty::after {
  content: '將分類拖曳至此處';
  color: #909399;
  font-size: 14px;
}

/* 拖曳控制按鈕 */
.tree-drag-toggle-btn {
  padding: 6px 16px;
  font-size: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  color: #409EFF;
  cursor: pointer;
  transition: all 0.3s;
}

.tree-drag-toggle-btn:hover {
  background-color: #ecf5ff;
}

.tree-drag-toggle-btn.disabled {
  background-color: #f56c6c;
  color: white;
  border-color: #f56c6c;
}

.tree-drag-toggle-btn.disabled:hover {
  background-color: #f78989;
}

/* 新增：為新按鈕添加樣式 */
.tree-add-btn {
  background-color: #409EFF;
  color: white;
}

.tree-add-btn:hover {
  background-color: #66b1ff;
}

/* 通用按鈕樣式 */
.tree-expand-btn, .tree-collapse-btn, .tree-drag-toggle-btn, .tree-add-btn {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  color: #606266;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s ease;
}

.tree-expand-btn:hover, .tree-collapse-btn:hover, .tree-drag-toggle-btn:hover:not(.disabled) {
  color: #409EFF;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
}

.tree-drag-toggle-btn.disabled {
  background-color: #f5f7fa; 
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}
</style> 